import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient, useQueries } from '@tanstack/react-query';
import { FlaskConical, Loader2, Play, RotateCw, AlertTriangle, Check, RefreshCw, Timer } from 'lucide-react';
import { HLE_MODELS, HLE_OUTPUT_COLUMN } from '@kolectra/shared/hle';
import type { HleModelId } from '@kolectra/shared/hle';
import { HLE_AUTO_RUN_INTERVALS } from '@kolectra/shared/schemas';
import type { HleItemRow, HleProbeResult, HleSourceSummary } from '@kolectra/shared/schemas';
import { CardSwitch, useConfirm } from '@kolectra/ui';
import { hleApi, type HleModelStatus } from '../lib/api-client.js';
import { PageHead } from '../components/page-head.js';

/** While a run is in flight, refresh on this cadence. Idle pages don't poll. */
const LIVE_POLL_MS = 5_000;

/** Display names + the sheet column each model's answer lands in (spec §4). */
const MODEL_LABEL: Record<HleModelId, string> = {
  gpt: 'GPT 5.2',
  gemini: 'Gemini 3.1 Pro',
  opus: 'Opus 4.6',
  kimi: 'Kimi K2.5',
  musespark: 'Muse Spark',
};
const MODEL_COLUMN: Record<HleModelId, string> = {
  gpt: 'O', gemini: 'P', opus: 'Q', kimi: 'R', musespark: 'S',
};

const intervalLabel = (m: number) =>
  m < 60 ? `${m} min` : m === 60 ? 'hourly' : m === 1440 ? 'daily' : `every ${m / 60}h`;

export function HleDashboardPage() {
  const sources = useQuery({
    queryKey: ['hle', 'sources'],
    queryFn: () => hleApi.sources(),
    refetchInterval: (query) =>
      (query.state.data ?? []).some((s: HleSourceSummary) => s.running) ? LIVE_POLL_MS : false,
  });

  const configured = useMemo(
    () => (sources.data ?? []).filter((s) => s.enabled && s.spreadsheetId),
    [sources.data],
  );

  // Counts for the switcher badges — one cheap read per language, cached by react-query.
  const progressQueries = useQueries({
    queries: configured.map((s) => ({
      queryKey: ['hle', 'progress', s.languageCode],
      queryFn: () => hleApi.progress(s.languageCode),
      refetchInterval: s.running ? LIVE_POLL_MS : (false as const),
    })),
  });
  const readyCounts = progressQueries.map((q) =>
    q.data ? q.data.readyItems - q.data.fullyTested : -1,
  );

  const [picked, setPicked] = useState('');
  // Default to the language with the most waiting work, not the alphabetical first.
  const ordered = useMemo(
    () =>
      configured
        .map((s, i) => ({ s, ready: readyCounts[i] ?? -1, p: progressQueries[i]?.data }))
        .sort((a, b) => b.ready - a.ready),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [configured, readyCounts.join(',')],
  );
  const active = configured.find((s) => s.languageCode === picked) ?? ordered[0]?.s;
  const running = configured.filter((s) => s.running).length;

  // The busiest language is only known once the sheet reads land, and the ordering
  // shifts as they do. Pin the choice the first time it is real, so the page does not
  // swap out from under someone who has already started reading it.
  // Wait for EVERY read, not the first: pinning on a partial result would capture
  // whichever language happened to answer first rather than the busiest one.
  const settled = progressQueries.length > 0 && progressQueries.every((q) => !q.isPending);
  useEffect(() => {
    if (picked || !settled) return;
    const best = ordered[0]?.s.languageCode;
    if (best) setPicked(best);
  }, [picked, settled, ordered]);

  return (
    <>
      <PageHead
        crumb="Project · HLE"
        title="Run the locked test"
        lead={
          running > 0
            ? `${running} language${running === 1 ? '' : 's'} testing now. Every answer is written straight into its O–S column; grading stays human.`
            : 'Five frontier models answer each item marked ready, under one locked prompt. Every answer is written straight into its O–S column; grading stays human.'
        }
      />
      <div className="pad" style={{ maxWidth: 1000 }}>
        <ModelRail />

        {sources.isLoading && <div className="empty">Loading languages…</div>}
        {sources.isError && <div className="banner error">Couldn’t load the language list. Reload to try again.</div>}

        {sources.isSuccess && configured.length === 0 && (
          <div className="tablewrap" style={{ marginTop: 22 }}>
            <div className="empty">
              No language is set up yet. Add a sheet on <strong>HLE languages</strong>, tick Enabled, and it appears here.
            </div>
          </div>
        )}

        {ordered.length > 1 && (
          <div style={{ marginTop: 22 }}>
            <CardSwitch
              label="Viewing language"
              value={active?.languageCode ?? ''}
              onChange={setPicked}
              options={ordered.map(({ s, ready, p }) => ({
                value: s.languageCode,
                label: s.languageName,
                badge: Math.max(ready, 0),
                muted: ready === 0,
                sub: p
                  ? `${Math.max(ready, 0)} to test · ${p.fullyTested} tested`
                  : s.running ? 'testing…' : 'reading sheet…',
              }))}
            />
          </div>
        )}

        {active && <LanguageRunner key={active.languageCode} source={active} />}
      </div>
    </>
  );
}

/* ---------------------------------------------------------------------------
   The model rail — five slots, fixed in sheet-column order. It answers, in one
   glance, the question a run can't answer cheaply: is each model going to work?
   ------------------------------------------------------------------------- */
function ModelRail() {
  const models = useQuery({ queryKey: ['hle', 'models'], queryFn: () => hleApi.models() });
  const [results, setResults] = useState<Record<string, HleProbeResult> | null>(null);
  const probe = useMutation({
    mutationFn: () => hleApi.probe(),
    onSuccess: (r) => setResults(Object.fromEntries(r.results.map((x) => [x.model, x]))),
  });

  const byId = new Map((models.data ?? []).map((m) => [m.model, m]));
  const ready = (models.data ?? []).filter((m) => m.configured).length;

  return (
    <section>
      <div className="toolbar" style={{ marginBottom: 10 }}>
        <div>
          <div style={{ fontWeight: 500, fontSize: 14 }}>Models</div>
          <div style={{ fontSize: 12, color: 'var(--ink-soft)', marginTop: 2 }}>
            {models.isSuccess
              ? `${ready} of ${models.data.length} have an API key · temperature 0 · effort medium · prompt locked`
              : 'Checking which models are configured…'}
          </div>
        </div>
        <div className="grow" />
        <button className="btn" disabled={probe.isPending || ready === 0} onClick={() => probe.mutate()}>
          {probe.isPending ? <Loader2 className="spin" /> : <FlaskConical />}
          {probe.isPending ? 'Checking…' : 'Check models'}
        </button>
      </div>

      <div className="hle-rail">
        {HLE_MODELS.map((id) => (
          <ModelSlot key={id} id={id} status={byId.get(id)} probe={results?.[id]} probing={probe.isPending} />
        ))}
      </div>

      {probe.isError && (
        <div className="banner error" style={{ marginTop: 10 }}>
          The check couldn’t run. {(probe.error as Error).message}
        </div>
      )}
    </section>
  );
}

function ModelSlot({
  id, status, probe, probing,
}: { id: HleModelId; status?: HleModelStatus; probe?: HleProbeResult; probing: boolean }) {
  const configured = status?.configured ?? false;
  const state = !configured ? 'off' : probe ? (probe.ok ? 'ready' : 'failed') : 'ready';

  const line = !configured
    ? `set ${status?.apiKeyEnv ?? 'its API key'}`
    : probing
      ? 'checking…'
      : probe
        ? probe.ok ? `answered in ${(probe.ms / 1000).toFixed(1)}s` : probe.error ?? 'failed'
        : 'key set · not checked';

  return (
    <div className={`hle-slot ${state}`} title={probe && !probe.ok ? probe.error ?? '' : undefined}>
      <div className="col">
        Col {MODEL_COLUMN[id]} · {HLE_OUTPUT_COLUMN[id].replace('_MODEL_OUTPUT', '')}
      </div>
      <div className="nm">{MODEL_LABEL[id]}</div>
      <div className="id">{status?.modelId ?? '—'}</div>
      <div className="st">
        {probing && configured ? <Loader2 className="spin" style={{ width: 12, height: 12 }} /> : null}
        <span className="txt">{line}</span>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------------
   One language's work list: ready → not runnable → tested, with selection.
   ------------------------------------------------------------------------- */
function LanguageRunner({ source }: { source: HleSourceSummary }) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const itemsQ = useQuery({
    queryKey: ['hle', 'items', source.languageCode],
    queryFn: () => hleApi.items(source.languageCode),
    // Poll while a run is in flight so rows migrate from ready to tested as cells fill.
    refetchInterval: source.running ? LIVE_POLL_MS : false,
  });

  const run = useMutation({
    mutationFn: (v: { rows?: number[]; force?: boolean }) => hleApi.run(source.languageCode, v),
    onSuccess: () => {
      setSelected(new Set());
      void qc.invalidateQueries({ queryKey: ['hle', 'sources'] });
      void qc.invalidateQueries({ queryKey: ['hle', 'items', source.languageCode] });
      void qc.invalidateQueries({ queryKey: ['hle', 'progress', source.languageCode] });
    },
  });

  useEffect(() => setSelected(new Set()), [source.languageCode]);

  const data = itemsQ.data;
  const unreadable = data?.sheetError ?? null;
  const missing = data?.missingInput ?? [];
  const blockedSheet = !!unreadable || missing.length > 0;

  const all = data?.items ?? [];
  const ready = all.filter((i) => !i.error && i.filled.length < HLE_MODELS.length);
  const notRunnable = all.filter((i) => i.error);
  const tested = all.filter((i) => !i.error && i.filled.length === HLE_MODELS.length);

  const selectedRows = ready.filter((i) => selected.has(i.row)).map((i) => i.row);
  const allChecked = ready.length > 0 && ready.every((i) => selected.has(i.row));
  const busy = source.running || run.isPending;

  function toggle(row: number) {
    setSelected((s) => { const n = new Set(s); if (n.has(row)) n.delete(row); else n.add(row); return n; });
  }
  function toggleExpand(row: number) {
    setExpanded((s) => { const n = new Set(s); if (n.has(row)) n.delete(row); else n.add(row); return n; });
  }

  /** Every item costs five frontier calls, so make the spend explicit before starting. */
  function confirmRun(count: number, force = false): Promise<boolean> {
    return confirm({
      title: count === 1 ? 'Run this item?' : `Run ${count} items?`,
      message:
        `${count} item${count === 1 ? '' : 's'} × 5 models = up to ${count * 5} model calls. ` +
        (force
          ? 'Answers already in the sheet will be replaced.'
          : 'Answers already in the sheet are kept — only blank cells are filled.'),
      confirmLabel: 'Run',
    });
  }

  async function runRows(rows: number[], force = false) {
    if (rows.length === 0 || !(await confirmRun(rows.length, force))) return;
    run.mutate({ rows, force });
  }
  async function runAllReady() {
    if (ready.length === 0 || !(await confirmRun(ready.length))) return;
    run.mutate({});
  }

  return (
    <div style={{ marginTop: 18 }}>
      <AutoTestBar source={source} />

      {source.running && source.runProgress && (
        <div className="runprog">
          <Loader2 className="spin" style={{ width: 15, height: 15 }} />
          <div className="rtrack">
            <i style={{ width: `${pctDone(source.runProgress.itemsDone, source.runProgress.itemsConsidered)}%` }} />
          </div>
          <span className="rct">
            Running {source.runProgress.itemsDone} / {source.runProgress.itemsConsidered}…
          </span>
        </div>
      )}

      {unreadable && (
        <div className="hle-note" style={{ marginTop: 0, marginBottom: 12 }}>
          <AlertTriangle style={{ width: 13, height: 13, flex: 'none', alignSelf: 'center' }} />
          <span>Can’t open this sheet — share it with the sync service account, then check the id and tab.</span>
          <span className="why">{unreadable}</span>
        </div>
      )}
      {!unreadable && missing.length > 0 && (
        <div className="hle-note" style={{ marginTop: 0, marginBottom: 12 }}>
          <AlertTriangle style={{ width: 13, height: 13, flex: 'none', alignSelf: 'center' }} />
          <span>Add a <strong>{missing.join(', ')}</strong> column to this tab, then tick the rows to test.</span>
          <span className="why">not part of the delivery template</span>
        </div>
      )}
      {run.isError && <div className="banner error" style={{ marginBottom: 12 }}>{(run.error as Error).message}</div>}
      {run.data && !run.data.started && (
        <div className="banner info" style={{ marginBottom: 12 }}>{run.data.message}</div>
      )}

      <div className="toolbar" style={{ marginBottom: 12, gap: 8 }}>
        <input
          type="checkbox"
          checked={allChecked}
          onChange={() => setSelected(allChecked ? new Set() : new Set(ready.map((i) => i.row)))}
          disabled={ready.length === 0 || busy}
          title="Select all"
          aria-label="Select every ready item"
        />
        <strong style={{ fontFamily: 'var(--disp)', fontSize: 16, fontWeight: 500 }}>Ready to test</strong>
        <span className="statepill" style={{ background: 'var(--hairline)', color: 'var(--ink-soft)' }}>
          {ready.length}
        </span>
        <div className="grow" />
        <button
          className="btn"
          onClick={() => void itemsQ.refetch()}
          disabled={itemsQ.isFetching}
          title="Re-read the sheet to pick up newly ticked rows"
        >
          {itemsQ.isFetching ? <Loader2 className="spin" /> : <RefreshCw />}
          Refresh
        </button>
        <button
          className="btn"
          disabled={busy || blockedSheet || ready.length === 0}
          onClick={() => void runAllReady()}
        >
          <Play />
          Run all ready
        </button>
        <button
          className="btn pri"
          disabled={busy || blockedSheet || selectedRows.length === 0}
          onClick={() => void runRows(selectedRows)}
        >
          {busy ? <Loader2 className="spin" /> : <Play />}
          Run selected{selectedRows.length > 0 ? ` (${selectedRows.length})` : ''}
        </button>
      </div>

      {itemsQ.isLoading ? (
        <div className="empty">
          <Loader2 className="spin" style={{ width: 14, height: 14, verticalAlign: -2, marginRight: 6 }} />
          Reading the sheet…
        </div>
      ) : itemsQ.isError ? (
        <div className="banner error">Couldn’t read this language’s sheet.</div>
      ) : ready.length === 0 ? (
        <div className="empty">
          {blockedSheet
            ? 'Nothing to show until the sheet is reachable.'
            : tested.length > 0
              ? 'Every ready item has all five answers. Tick more rows as READY_FOR_TEST in the sheet, then Refresh.'
              : 'No item is marked READY_FOR_TEST yet. Tick the rows to test in the sheet, then Refresh.'}
        </div>
      ) : (
        <div className="reclist" style={{ marginBottom: 26 }}>
          {ready.map((item) => (
            <ItemRow
              key={item.row}
              item={item}
              checked={selected.has(item.row)}
              onToggle={() => toggle(item.row)}
              expanded={expanded.has(item.row)}
              onExpand={() => toggleExpand(item.row)}
              disabled={busy || blockedSheet}
              onRun={() => void runRows([item.row])}
            />
          ))}
        </div>
      )}

      {notRunnable.length > 0 && (
        <>
          <SectionLabel title="Marked ready · not runnable" count={notRunnable.length} />
          <div className="reclist" style={{ marginBottom: 26 }}>
            {notRunnable.map((item) => (
              <div key={item.row} className="hle-item bad">
                <AlertTriangle style={{ width: 14, height: 14, color: 'var(--bad)' }} />
                <span className="idx">{item.itemId || `row ${item.row}`}</span>
                <span className="why">{item.error}</span>
                <ModelDots filled={item.filled} />
                <span />
              </div>
            ))}
          </div>
        </>
      )}

      <SectionLabel title="Tested · all five answered" count={tested.length} />
      {tested.length === 0 ? (
        <div className="empty">Nothing fully tested yet.</div>
      ) : (
        <div className="reclist">
          {tested.map((item) => (
            <div key={item.row} className="hle-item">
              <Check style={{ width: 14, height: 14, color: 'var(--data)' }} />
              <span className="idx">{item.itemId || `row ${item.row}`}</span>
              <span className="q" title={item.question}>{item.question}</span>
              <ModelDots filled={item.filled} />
              <button
                className="btn sm"
                disabled={busy || blockedSheet}
                title="Replace all five answers for this item"
                onClick={() => void runRows([item.row], true)}
              >
                <RotateCw />
                Re-run
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Unattended testing for this language. */
function AutoTestBar({ source }: { source: HleSourceSummary }) {
  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: (patch: { autoRun?: boolean; autoRunIntervalMinutes?: number }) =>
      hleApi.upsertSource(source.languageCode, patch),
    onSuccess: (rows) => qc.setQueryData(['hle', 'sources'], rows),
  });

  const next = source.lastAutoRunAt
    ? new Date(new Date(source.lastAutoRunAt).getTime() + source.autoRunIntervalMinutes * 60_000)
    : null;
  const every = intervalLabel(source.autoRunIntervalMinutes);

  return (
    <div className={`hle-autobar ${source.autoRun ? 'on' : ''}`}>
      <Timer style={{ width: 15, height: 15, color: source.autoRun ? 'var(--data)' : 'var(--ink-faint)' }} />
      <button
        type="button"
        role="switch"
        aria-checked={source.autoRun}
        aria-label="Test automatically"
        className={`switch ${source.autoRun ? 'on' : ''}`}
        disabled={save.isPending}
        onClick={() => save.mutate({ autoRun: !source.autoRun })}
      >
        <i />
      </button>
      <span>Test automatically</span>
      <select
        value={source.autoRunIntervalMinutes}
        disabled={save.isPending}
        aria-label="How often to test automatically"
        onChange={(e) => save.mutate({ autoRunIntervalMinutes: Number(e.target.value) })}
      >
        {HLE_AUTO_RUN_INTERVALS.map((m) => (
          <option key={m} value={m}>{intervalLabel(m)}</option>
        ))}
      </select>
      <span className="grow" style={{ flex: 1 }} />
      <span className="next">
        {source.autoRun
          ? next
            ? `${every} · next around ${next.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
            : `${every} · first check shortly`
          : 'off — items are tested only when you press Run'}
      </span>
    </div>
  );
}

function ItemRow({
  item, checked, onToggle, expanded, onExpand, disabled, onRun,
}: {
  item: HleItemRow;
  checked: boolean;
  onToggle: () => void;
  expanded: boolean;
  onExpand: () => void;
  disabled: boolean;
  onRun: () => void;
}) {
  return (
    <div className={`hle-item ${checked ? 'picked' : ''} ${expanded ? 'open' : ''}`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        disabled={disabled}
        aria-label={`Select item ${item.itemId || item.row}`}
      />
      <span className="idx">{item.itemId || `row ${item.row}`}</span>
      <span
        className="q"
        role="button"
        tabIndex={0}
        title={expanded ? 'Collapse' : item.question}
        onClick={onExpand}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onExpand(); } }}
      >
        <span className="tag" style={{ marginRight: 8 }}>{item.answerType}</span>
        {item.question}
      </span>
      <ModelDots filled={item.filled} />
      <button className="btn sm" disabled={disabled} onClick={onRun}>
        <Play />
        Run
      </button>
    </div>
  );
}

/** Five lamps in sheet-column order — which of O–S already hold an answer. */
function ModelDots({ filled }: { filled: HleModelId[] }) {
  const has = new Set(filled);
  return (
    <span
      className="hle-dots"
      title={HLE_MODELS.map((m) => `${MODEL_COLUMN[m]} · ${MODEL_LABEL[m]}: ${has.has(m) ? 'answered' : 'blank'}`).join('\n')}
    >
      {HLE_MODELS.map((m) => <i key={m} className={has.has(m) ? 'on' : ''} />)}
    </span>
  );
}

function SectionLabel({ title, count }: { title: string; count: number }) {
  return (
    <div className="toolbar" style={{ marginBottom: 10, gap: 8 }}>
      <strong style={{ fontFamily: 'var(--disp)', fontSize: 15, fontWeight: 500 }}>{title}</strong>
      <span className="statepill" style={{ background: 'var(--hairline)', color: 'var(--ink-soft)' }}>{count}</span>
    </div>
  );
}

const pctDone = (done: number, total: number) => (total > 0 ? Math.round((done / total) * 100) : 0);
